import { Category } from '@/models/Category'
import { CategoryLevel } from '@/models/CategoryLevel'
import { SimpleLookupItem } from '@/models/SimpleLookupItem'
import axios from 'axios'
import { AbstractService } from './AbstractService'
import { StorageType } from './StorageService'

export class LookupService extends AbstractService {
  private static api = `${process.env.VUE_APP_BASE_URL}/Umbraco/Api/LookupApi/`

  private async getRegionsAsync (): Promise<SimpleLookupItem[]> {
    try {
      const response = await axios.get(`${LookupService.api}GetRegions`)
      return super.parseList<SimpleLookupItem>(response)
    } catch (error) {
      super.handleAxiosErrors(error, 'getRegionsAsync');
      return []
    }
  }

  private async getCountriesAsync (): Promise<SimpleLookupItem[]> {
    try {
      const response = await axios.get(`${LookupService.api}GetCountries`)
      return super.parseList<SimpleLookupItem>(response)
    } catch (error) {
      super.handleAxiosErrors(error, 'getCountriesAsync');
      return []
    }
  }

  private async getAustralianStatesAsync (includeOtherOption: boolean): Promise<SimpleLookupItem[]> {
    try {
      const response = await axios.get(`${LookupService.api}GetAustralianStates?includeOtherOption=${includeOtherOption ? "true" : "false"}`)
      return super.parseList<SimpleLookupItem>(response)
    } catch (error) {
      super.handleAxiosErrors(error, 'getAustralianStatesAsync');
      return []
    }
  }

  private async getCategoriesAsync (parentCategory: number): Promise<Category[]> {
    try {      
      const response = await axios.get(`${LookupService.api}GetCategories?categoryId=${parentCategory}&hideCategoriesWithoutProducts=false`)
      return super.parseList<Category>(response)
    } catch (error) {
      super.handleAxiosErrors(error, 'getCategoriesAsync');
      return []
    }
  }

  private async getCategoryAsync (categoryId: number): Promise<Category | null> {
    try {
      const response = await axios.get(`${LookupService.api}GetCategory`, {
        params: {
          categoryId: categoryId
         }
      })
      return super.parseObject<Category>(response)
    } catch (error) {
      super.handleAxiosErrors(error, 'getCategoriesAsync');
      return null
    }
  }

  private async getMajorRetailersAsync (): Promise<SimpleLookupItem[]> {
    try {
      const response = await axios.get(`${LookupService.api}GetMajorRetailers`)
      return super.parseList<SimpleLookupItem>(response)
    } catch (error) {
      super.handleAxiosErrors(error, 'getMajorRetailersAsync');
      return []
    }
  }

  private async getIndustryTypeAsync (): Promise<SimpleLookupItem[]> {
    try {
      const response = await axios.get(`${LookupService.api}GetIndustryType`)
      return super.parseList<SimpleLookupItem>(response)
    } catch (error) {
      super.handleAxiosErrors(error, 'getIndustryTypeAsync');
      return []
    }
  }

  private async getCategoryLevelIds (categoryId: number): Promise<CategoryLevel | null> {
    try {
      const response = await axios.get(`${LookupService.api}GetCategoryLevelIds`, {
        params: {
          categoryId: categoryId
         }
      })
      return super.parseObject<CategoryLevel>(response)
    } catch (error) {      
      super.handleAxiosErrors(error, 'getCategoryLevelIds');
      return null
    }
  }

  async GetRegions (): Promise<SimpleLookupItem[]> {
    return await this.getLookupData('regions', StorageType.Local, this.getRegionsAsync)
  }

  async GetCountries (): Promise<SimpleLookupItem[]> {
    return await this.getLookupData('countries', StorageType.Local,  this.getCountriesAsync)
  }

  async GetAustralianStates (includeOtherOption: boolean): Promise<SimpleLookupItem[]> {
    return await this.getLookupData('states', StorageType.Local,  this.getAustralianStatesAsync, includeOtherOption)
  }

  async GetCategories (parentCategory: number): Promise<Category[]> {
    return await this.getLookupData(`categories_${parentCategory}`, StorageType.Session,  this.getCategoriesAsync, parentCategory)
  }

  async GetCategory (categoryId: number): Promise<Category | null> {
    return await this.getCategoryAsync(categoryId)
  }

  async GetMajorRetailers (): Promise<SimpleLookupItem[]> {
    return await this.getLookupData(`major_retailers`, StorageType.Local, this.getMajorRetailersAsync)
  }

  async GetIndustryType (): Promise<SimpleLookupItem[]> {
    return await this.getLookupData(`industry_type`, StorageType.Local, this.getIndustryTypeAsync)
  }

  async GetCategoryLevelIds (categoryId: number): Promise<CategoryLevel | null> {
    return await this.getCategoryLevelIds(categoryId)
  }

  
}
